import React, { useState, useEffect } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题区块样式
const HeaderSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 计算器容器样式
const CalculatorContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '2rem',
  
  '@media (min-width: 992px)': {
    gridTemplateColumns: '1fr 1fr'
  }
});

// 计算器表单样式
const CalculatorForm = styled('div', {
  padding: '2rem',
  background: '$backgroundSecondary',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
});

// 计算器结果样式
const CalculatorResults = styled('div', {
  padding: '2rem',
  background: '$backgroundSecondary',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
});

// 表单组样式
const FormGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
});

// 标签样式
const Label = styled('label', {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 帮助文本样式
const HelpText = styled('span', {
  fontSize: '0.875rem',
  color: '$textTertiary'
});

// 行内表单组样式
const InlineFormGroup = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
});

// 输入框样式
const Input = styled('input', {
  flex: 1,
  padding: '0.875rem',
  borderRadius: '8px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  fontSize: '1rem',
  color: '$textPrimary',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$accentPrimary'
  }
});

// 选择框样式
const Select = styled('select', {
  flex: 1,
  padding: '0.875rem',
  borderRadius: '8px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  fontSize: '1rem',
  color: '$textPrimary',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$accentPrimary'
  }
});

// 单位标签样式
const UnitLabel = styled('span', {
  fontSize: '1rem',
  color: '$textSecondary',
  whiteSpace: 'nowrap'
});

// 提交按钮样式
const SubmitButton = styled('button', {
  padding: '1rem 2rem',
  background: '$accentPrimary',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  marginTop: '1rem',
  
  '&:hover': {
    opacity: 0.9,
    transform: 'translateY(-2px)'
  }
});

// 重置按钮样式
const ResetButton = styled('button', {
  padding: '1rem 2rem',
  background: 'transparent',
  color: '$textSecondary',
  border: '1px solid $borderColor',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    background: '$backgroundTertiary'
  }
});

// 按钮组样式
const ButtonGroup = styled('div', {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'flex-end'
});

// 结果标题样式
const ResultTitle = styled('h2', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  marginBottom: '1rem'
});

// 结果项容器样式
const ResultItemsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 结果项样式
const ResultItem = styled('div', {
  padding: '1.25rem',
  background: '$backgroundPrimary',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

// 结果项标签样式
const ResultItemLabel = styled('span', {
  fontSize: '1rem',
  color: '$textSecondary'
});

// 结果项值样式
const ResultItemValue = styled('span', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 总收益样式
const TotalProfit = styled('div', {
  padding: '1.5rem',
  marginTop: '1rem',
  borderRadius: '8px',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

// 总收益标签样式
const TotalProfitLabel = styled('span', {
  fontSize: '1.1rem'
});

// 总收益值样式
const TotalProfitValue = styled('span', {
  fontSize: '1.75rem',
  fontWeight: 'bold'
});

// 注意事项样式
const Disclaimer = styled('p', {
  fontSize: '0.875rem',
  color: '$textTertiary',
  marginTop: '1rem',
  fontStyle: 'italic'
});

// 滑块容器样式
const SliderContainer = styled('div', {
  width: '100%',
  marginTop: '0.5rem'
});

// 滑块样式
const Slider = styled('input', {
  width: '100%',
  height: '6px',
  borderRadius: '3px',
  appearance: 'none',
  background: '$borderColor',
  outline: 'none',
  
  '&::-webkit-slider-thumb': {
    appearance: 'none',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '$accentPrimary',
    cursor: 'pointer'
  },
  
  '&::-moz-range-thumb': {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '$accentPrimary',
    cursor: 'pointer',
    border: 'none'
  }
});

// 滑块值显示样式
const SliderValue = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '0.5rem',
  fontSize: '0.875rem',
  color: '$textSecondary'
});

// 说明容器样式
const ExplanationContainer = styled('div', {
  marginTop: '3rem',
  padding: '2rem',
  background: '$backgroundSecondary',
  borderRadius: '12px'
});

// 说明标题样式
const ExplanationTitle = styled('h2', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  marginBottom: '1rem'
});

// 说明内容样式
const ExplanationContent = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6,
  marginBottom: '1rem'
});

// 说明列表样式
const ExplanationList = styled('ul', {
  paddingLeft: '1.5rem',
  marginBottom: '1rem'
});

// 说明列表项样式
const ExplanationListItem = styled('li', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6,
  marginBottom: '0.5rem'
});

/**
 * 云挖矿收益计算器组件
 * 计算不同参数下的挖矿收益
 */
const CloudMiningCalculator: React.FC = () => {
  const { t } = useTranslation();
  
  // 定义状态变量
  const [algorithm, setAlgorithm] = useState('sha256');
  const [hashrate, setHashrate] = useState(100);
  const [hashrateUnit, setHashrateUnit] = useState('TH/s');
  const [contractDuration, setContractDuration] = useState(360);
  const [maintenanceFee, setMaintenanceFee] = useState(0.001);
  const [electricityCost, setElectricityCost] = useState(0.05);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [networkDifficulty, setNetworkDifficulty] = useState(0);
  const [coinPrice, setCoinPrice] = useState(50000);
  const [priceTrend, setPriceTrend] = useState(0);
  
  // 计算结果状态
  const [dailyRevenue, setDailyRevenue] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [yearlyRevenue, setYearlyRevenue] = useState(0);
  const [contractRevenue, setContractRevenue] = useState(0);
  const [maintenanceCost, setMaintenanceCost] = useState(0);
  const [electricityCosts, setElectricityCosts] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [roi, setRoi] = useState(0);
  
  // 根据选择的算法更新单位
  useEffect(() => {
    switch (algorithm) {
      case 'sha256':
        setHashrateUnit('TH/s');
        break;
      case 'ethash':
        setHashrateUnit('MH/s');
        break;
      case 'scrypt':
      case 'x11':
        setHashrateUnit('GH/s');
        break;
      case 'randomx':
        setHashrateUnit('KH/s');
        break;
      default:
        setHashrateUnit('TH/s');
    }
  }, [algorithm]);
  
  // 计算挖矿收益
  const calculateMiningRevenue = () => {
    // 注意：这是一个简化的计算模型，实际挖矿收益计算更为复杂
    
    // 根据算法和算力计算每日基础收益（仅为演示，非准确计算）
    let baseReward = 0;
    
    switch (algorithm) {
      case 'sha256': // BTC
        baseReward = (hashrate * 0.000008); // TH/s
        break;
      case 'ethash': // ETH
        baseReward = (hashrate * 0.0002); // MH/s
        break;
      case 'scrypt': // LTC
        baseReward = (hashrate * 0.0001); // GH/s
        break;
      case 'x11': // DASH
        baseReward = (hashrate * 0.00005); // GH/s
        break;
      case 'randomx': // XMR
        baseReward = (hashrate * 0.0003); // KH/s
        break;
      default:
        baseReward = 0;
    }
    
    // 计算每日收益（考虑币价和趋势）
    const priceMultiplier = 1 + (priceTrend / 100);
    const adjustedPrice = coinPrice * priceMultiplier;
    
    const dailyRev = baseReward * adjustedPrice;
    const monthlyRev = dailyRev * 30;
    const yearlyRev = dailyRev * 365;
    const contractRev = dailyRev * contractDuration;
    
    // 计算维护成本
    const dailyMaintenance = hashrate * maintenanceFee;
    const totalMaintenance = dailyMaintenance * contractDuration;
    
    // 计算电力成本
    const dailyElectricity = hashrate * electricityCost;
    const totalElectricity = dailyElectricity * contractDuration;
    
    // 计算总利润和ROI
    const profit = contractRev - totalMaintenance - totalElectricity;
    const returnOnInvestment = ((profit / (totalMaintenance + totalElectricity)) * 100).toFixed(2);
    
    // 更新状态
    setDailyRevenue(parseFloat(dailyRev.toFixed(6)));
    setMonthlyRevenue(parseFloat(monthlyRev.toFixed(6)));
    setYearlyRevenue(parseFloat(yearlyRev.toFixed(6)));
    setContractRevenue(parseFloat(contractRev.toFixed(6)));
    setMaintenanceCost(parseFloat(totalMaintenance.toFixed(2)));
    setElectricityCosts(parseFloat(totalElectricity.toFixed(2)));
    setTotalProfit(parseFloat(profit.toFixed(6)));
    setRoi(parseFloat(returnOnInvestment));
  };
  
  // 重置计算器
  const resetCalculator = () => {
    setAlgorithm('sha256');
    setHashrate(100);
    setContractDuration(360);
    setMaintenanceFee(0.001);
    setElectricityCost(0.05);
    setNetworkDifficulty(0);
    setCoinPrice(50000);
    setPriceTrend(0);
  };
  
  // 处理算法变更
  const handleAlgorithmChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAlgorithm(e.target.value);
    
    // 根据算法预设币价
    switch (e.target.value) {
      case 'sha256': // BTC
        setCoinPrice(50000);
        break;
      case 'ethash': // ETH
        setCoinPrice(2500);
        break;
      case 'scrypt': // LTC
        setCoinPrice(100);
        break;
      case 'x11': // DASH
        setCoinPrice(70);
        break;
      case 'randomx': // XMR
        setCoinPrice(150);
        break;
    }
  };
  
  return (
    <PageContainer>
      <HeaderSection>
        <Title>{t('cloudMining.calculator.title', '挖矿收益计算器')}</Title>
        <Subtitle>
          {t('cloudMining.calculator.subtitle', '估算您的云挖矿潜在收益，帮助您做出明智的投资决策')}
        </Subtitle>
      </HeaderSection>
      
      <CalculatorContainer>
        {/* 计算器表单 */}
        <CalculatorForm>
          <FormGroup>
            <Label htmlFor="algorithm" id="algorithm-label">{t('cloudMining.calculator.algorithm', '挖矿算法')}</Label>
            <Select 
              id="algorithm" 
              value={algorithm} 
              onChange={handleAlgorithmChange}
              aria-labelledby="algorithm-label"
              name="algorithm"
            >
              <option value="sha256">SHA-256 (BTC, BCH)</option>
              <option value="ethash">Ethash (ETH, ETC)</option>
              <option value="scrypt">Scrypt (LTC, DOGE)</option>
              <option value="x11">X11 (DASH)</option>
              <option value="randomx">RandomX (XMR)</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="hashrate">{t('cloudMining.calculator.hashrate', '算力')}</Label>
            <InlineFormGroup>
              <Input 
                type="number" 
                id="hashrate" 
                value={hashrate} 
                onChange={(e) => setHashrate(parseFloat(e.target.value))}
                min="1"
                aria-label={t('cloudMining.calculator.hashrate', '算力')}
              />
              <UnitLabel>{hashrateUnit}</UnitLabel>
            </InlineFormGroup>
            <SliderContainer>
              <Slider 
                type="range" 
                min="1" 
                max="10000" 
                value={hashrate} 
                onChange={(e) => setHashrate(parseFloat(e.target.value))}
                aria-label={t('cloudMining.calculator.hashrate', '算力')}
              />
              <SliderValue>
                <span>1 {hashrateUnit}</span>
                <span>10,000 {hashrateUnit}</span>
              </SliderValue>
            </SliderContainer>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="contractDuration">{t('cloudMining.calculator.contractDuration', '合约时长 (天)')}</Label>
            <Input 
              type="number" 
              id="contractDuration" 
              value={contractDuration} 
              onChange={(e) => setContractDuration(parseInt(e.target.value))}
              min="30"
              max="1080"
              aria-label={t('cloudMining.calculator.contractDuration', '合约时长 (天)')}
            />
            <SliderContainer>
              <Slider 
                type="range" 
                min="30" 
                max="1080" 
                step="30" 
                value={contractDuration} 
                onChange={(e) => setContractDuration(parseInt(e.target.value))}
                aria-label={t('cloudMining.calculator.contractDuration', '合约时长 (天)')}
              />
              <SliderValue>
                <span>30 {t('cloudMining.calculator.days', '天')}</span>
                <span>1080 {t('cloudMining.calculator.days', '天')}</span>
              </SliderValue>
            </SliderContainer>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="maintenanceFee">{t('cloudMining.calculator.maintenanceFee', '每日维护费 ($/单位算力)')}</Label>
            <Input 
              type="number" 
              id="maintenanceFee" 
              value={maintenanceFee} 
              onChange={(e) => setMaintenanceFee(parseFloat(e.target.value))}
              step="0.0001"
              min="0"
              aria-label={t('cloudMining.calculator.maintenanceFee', '每日维护费 ($/单位算力)')}
            />
            <HelpText>{t('cloudMining.calculator.maintenanceFeeHelp', '数据中心设备维护和管理费用')}</HelpText>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="electricityCost">{t('cloudMining.calculator.electricityCost', '每日电力成本 ($/单位算力)')}</Label>
            <Input 
              type="number" 
              id="electricityCost" 
              value={electricityCost} 
              onChange={(e) => setElectricityCost(parseFloat(e.target.value))}
              step="0.01"
              min="0"
              aria-label={t('cloudMining.calculator.electricityCost', '每日电力成本 ($/单位算力)')}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="coinPrice">{t('cloudMining.calculator.coinPrice', '当前币价 ($)')}</Label>
            <Input 
              type="number" 
              id="coinPrice" 
              value={coinPrice} 
              onChange={(e) => setCoinPrice(parseFloat(e.target.value))}
              min="0"
              aria-label={t('cloudMining.calculator.coinPrice', '当前币价 ($)')}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="priceTrend">{t('cloudMining.calculator.priceTrend', '价格趋势预期 (%)')}</Label>
            <Input 
              type="number" 
              id="priceTrend" 
              value={priceTrend} 
              onChange={(e) => setPriceTrend(parseFloat(e.target.value))}
              min="-100"
              max="1000"
              aria-label={t('cloudMining.calculator.priceTrend', '价格趋势预期 (%)')}
            />
            <HelpText>{t('cloudMining.calculator.priceTrendHelp', '合约期间预期的价格变动百分比')}</HelpText>
            <SliderContainer>
              <Slider 
                type="range" 
                min="-50" 
                max="200" 
                value={priceTrend} 
                onChange={(e) => setPriceTrend(parseFloat(e.target.value))}
                aria-label={t('cloudMining.calculator.priceTrend', '价格趋势预期 (%)')}
              />
              <SliderValue>
                <span>-50%</span>
                <span>+200%</span>
              </SliderValue>
            </SliderContainer>
          </FormGroup>
          
          <ButtonGroup>
            <ResetButton type="button" onClick={resetCalculator}>
              {t('cloudMining.calculator.reset', '重置')}
            </ResetButton>
            <SubmitButton type="button" onClick={calculateMiningRevenue}>
              {t('cloudMining.calculator.calculate', '计算收益')}
            </SubmitButton>
          </ButtonGroup>
        </CalculatorForm>
        
        {/* 计算结果 */}
        <CalculatorResults>
          <ResultTitle>{t('cloudMining.calculator.results', '收益预估')}</ResultTitle>
          
          <ResultItemsContainer>
            <ResultItem>
              <ResultItemLabel>{t('cloudMining.calculator.dailyRevenue', '每日收益')}</ResultItemLabel>
              <ResultItemValue>${dailyRevenue.toFixed(6)}</ResultItemValue>
            </ResultItem>
            
            <ResultItem>
              <ResultItemLabel>{t('cloudMining.calculator.monthlyRevenue', '每月收益')}</ResultItemLabel>
              <ResultItemValue>${monthlyRevenue.toFixed(6)}</ResultItemValue>
            </ResultItem>
            
            <ResultItem>
              <ResultItemLabel>{t('cloudMining.calculator.yearlyRevenue', '每年收益')}</ResultItemLabel>
              <ResultItemValue>${yearlyRevenue.toFixed(6)}</ResultItemValue>
            </ResultItem>
            
            <ResultItem>
              <ResultItemLabel>{t('cloudMining.calculator.contractRevenue', '合约总收益')}</ResultItemLabel>
              <ResultItemValue>${contractRevenue.toFixed(6)}</ResultItemValue>
            </ResultItem>
            
            <ResultItem>
              <ResultItemLabel>{t('cloudMining.calculator.maintenanceCost', '维护费总计')}</ResultItemLabel>
              <ResultItemValue>-${maintenanceCost.toFixed(2)}</ResultItemValue>
            </ResultItem>
            
            <ResultItem>
              <ResultItemLabel>{t('cloudMining.calculator.electricityCost', '电力成本总计')}</ResultItemLabel>
              <ResultItemValue>-${electricityCosts.toFixed(2)}</ResultItemValue>
            </ResultItem>
          </ResultItemsContainer>
          
          <TotalProfit>
            <TotalProfitLabel>{t('cloudMining.calculator.totalProfit', '净收益')}</TotalProfitLabel>
            <TotalProfitValue>${totalProfit.toFixed(6)}</TotalProfitValue>
          </TotalProfit>
          
          <ResultItem>
            <ResultItemLabel>{t('cloudMining.calculator.roi', '投资回报率 (ROI)')}</ResultItemLabel>
            <ResultItemValue>{roi}%</ResultItemValue>
          </ResultItem>
          
          <Disclaimer>
            {t('cloudMining.calculator.disclaimer', '* 此计算器仅提供估算，实际收益可能因网络难度变化、币价波动和其他因素而有所不同。')}
          </Disclaimer>
        </CalculatorResults>
      </CalculatorContainer>
      
      <ExplanationContainer>
        <ExplanationTitle>{t('cloudMining.calculator.howItWorks', '收益计算说明')}</ExplanationTitle>
        <ExplanationContent>
          {t('cloudMining.calculator.explanationIntro', '云挖矿收益受多种因素影响，我们的计算器考虑了以下主要变量：')}
        </ExplanationContent>
        
        <ExplanationList>
          <ExplanationListItem>
            {t('cloudMining.calculator.explanation1', '算力大小：直接影响挖矿速度和收益。')}
          </ExplanationListItem>
          <ExplanationListItem>
            {t('cloudMining.calculator.explanation2', '挖矿算法：不同算法对应不同币种，有不同的难度和收益率。')}
          </ExplanationListItem>
          <ExplanationListItem>
            {t('cloudMining.calculator.explanation3', '合约时长：合约持续的天数，影响总收益。')}
          </ExplanationListItem>
          <ExplanationListItem>
            {t('cloudMining.calculator.explanation4', '维护费用：数据中心设备维护和管理的费用。')}
          </ExplanationListItem>
          <ExplanationListItem>
            {t('cloudMining.calculator.explanation5', '电力成本：数据中心运行的电力消耗成本。')}
          </ExplanationListItem>
          <ExplanationListItem>
            {t('cloudMining.calculator.explanation6', '币价：挖矿所得加密货币的当前市场价值。')}
          </ExplanationListItem>
          <ExplanationListItem>
            {t('cloudMining.calculator.explanation7', '价格趋势：合约期间加密货币价格的预期变化。')}
          </ExplanationListItem>
        </ExplanationList>
        
        <ExplanationContent>
          {t('cloudMining.calculator.explanationNote', '注意：挖矿收益会随着网络难度的增加而降低。随着越来越多的矿工加入网络，难度会自动调整以维持稳定的出块时间。此计算器默认考虑了适度的难度增长因素。')}
        </ExplanationContent>
      </ExplanationContainer>
    </PageContainer>
  );
};

export default CloudMiningCalculator; 